import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Flame, 
  MapPin, 
  Search, 
  ArrowLeft, 
  Users, 
  Heart,
  Star
} from 'lucide-react';
import { supportedCities, supportedRegions } from '../data/supportedCities';
import { BrandLockup } from './BrandLogo';

const SupportedCitiesPage = ({ onBack, onGetStarted }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');

  // Filter cities based on search and region
  const filteredCities = supportedCities.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'All' || city.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  // Group cities by region for better organization
  const citysByRegion = filteredCities.reduce((acc, city) => {
    if (!acc[city.region]) {
      acc[city.region] = [];
    }
    acc[city.region].push(city);
    return acc;
  }, {});

  const regionColors = {
    "Northeast US": "from-blue-500 to-indigo-500",
    "West Coast US": "from-purple-500 to-violet-500", 
    "Midwest US": "from-green-500 to-emerald-500",
    "South US": "from-red-500 to-pink-500",
    "Southeast US": "from-pink-500 to-rose-500",
    "Southwest US": "from-orange-500 to-amber-500",
    "Pacific Northwest US": "from-teal-500 to-cyan-500",
    "Mountain West US": "from-indigo-500 to-purple-500",
    "Pacific": "from-cyan-500 to-blue-500",
    "Asia": "from-yellow-500 to-orange-500",
    "Canada": "from-red-600 to-rose-600"
  };

  const regionStats = {
    "Northeast US": { icon: "🏙️" },
    "West Coast US": { icon: "🌊" },
    "Midwest US": { icon: "🌾" },
    "South US": { icon: "🌴" },
    "Southeast US": { icon: "🍑" },
    "Southwest US": { icon: "🌵" },
    "Pacific Northwest US": { icon: "🏔️" },
    "Mountain West US": { icon: "⛰️" },
    "Pacific": { icon: "🏝️" },
    "Asia": { icon: "🏯" },
    "Canada": { icon: "🍁" }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Navigation */}
      <nav className="relative z-50 bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={onBack} className="mr-2" aria-label="Back" data-testid="cities-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <BrandLockup size="md" testId="cities-brand-lockup" />
            </div>
            
            <Button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-2 rounded-full"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-6 bg-gradient-to-r from-pink-100 to-red-100 text-red-700 border-red-200 px-4 py-2">
            <MapPin className="w-4 h-4 mr-2" />
            Global Coverage
          </Badge>
          
          <h1 className="text-4xl lg:text-6xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-red-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              50 Major Global Cities
            </span>
          </h1>
          
          <p className="text-xl lg:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
            FlameLink connects singles across 
            <span className="font-semibold text-red-600"> North America</span>, 
            <span className="font-semibold text-blue-600"> Asia</span>, and the 
            <span className="font-semibold text-green-600"> Pacific</span>, bringing 
            <span className="font-semibold text-purple-600"> meaningful connections</span> to every city we serve.
          </p>

          {/* Search and Filter Controls */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search cities, countries, or regions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-3 w-full rounded-full border-2 border-pink-200 focus:border-red-400"
                />
              </div>
              
              <div className="flex gap-2 flex-wrap justify-center">
                <Button
                  variant={selectedRegion === 'All' ? 'default' : 'outline'}
                  onClick={() => setSelectedRegion('All')}
                  className="rounded-full"
                >
                  All Regions
                </Button>
                {supportedRegions.slice(0, 5).map(region => (
                  <Button
                    key={region}
                    variant={selectedRegion === region ? 'default' : 'outline'}
                    onClick={() => setSelectedRegion(region)}
                    className="rounded-full hidden md:inline-flex"
                  >
                    {regionStats[region]?.icon} {region.replace(' US', '')}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cities Grid by Region */}
        <div className="space-y-12">
          {Object.entries(citysByRegion).map(([region, cities]) => (
            <div key={region} className="space-y-6">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {regionStats[region]?.icon} {region}
                </h2>
                <p className="text-gray-600">
                  {cities.length} {cities.length === 1 ? 'city' : 'cities'} • 
                  {cities.reduce((sum, city) => {
                    const pop = parseFloat(city.population.replace(/[KM]/g, ''));
                    const multiplier = city.population.includes('M') ? 1 : 0.001;
                    return sum + (pop * multiplier);
                  }, 0).toFixed(1)}M+ people
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {cities.map((city, index) => (
                  <Card key={`${city.name}-${index}`} className="p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-transparent hover:border-pink-200">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {city.name}
                        </h3>
                        <Badge 
                          className={`bg-gradient-to-r ${regionColors[region] || 'from-gray-400 to-gray-500'} text-white`}
                        >
                          {city.country}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        <span className="text-sm">{city.population} residents</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center text-gray-500">
                          <MapPin className="w-3 h-3 mr-1" />
                          {city.region}
                        </div>
                        <div className="flex items-center text-green-600">
                          <Heart className="w-3 h-3 mr-1" />
                          <span className="font-medium">Active</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="p-8 bg-gradient-to-r from-red-600 via-pink-600 to-orange-600 text-white">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Find Love in Your City?
              </h2>
              <p className="text-xl mb-6 text-pink-100">
                Meet verified singles in your area and start making meaningful connections today.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={onGetStarted}
                  className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-full text-lg font-semibold"
                >
                  <Flame className="w-5 h-5 mr-2" />
                  Start Dating Now
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-3 rounded-full text-lg font-semibold"
                >
                  <Star className="w-5 h-5 mr-2" />
                  Learn More
                </Button>
              </div>
              
              <p className="text-pink-200 mt-4 text-sm">
                Free to join • Available in {supportedCities.length} cities • Verified profiles only
              </p>
            </div>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600">
          <p className="text-sm">
            Don't see your city? We're expanding rapidly. 
            <a href="mailto:hello@flamelink.com" className="text-red-600 hover:text-red-800 font-medium ml-1">
              Let us know where you'd like FlameLink next!
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportedCitiesPage;